"use client";
import Image from '@/components/ui/MediaImage';
import React from "react";
import { motion } from "framer-motion";
import { transformMediaUrl } from "@/lib/media-url-transformer";

interface RightTwoImageBottomTextCtaProps {
    leftImage?: { mediaId?: number; url?: string; alt?: string } | null;
    rightImage?: { mediaId?: number; url?: string; alt?: string } | null;
    title?: string;
    description?: string;
}

export default function RightTwoImageBottomTextCta({ 
    leftImage,
    rightImage,
    title = 'Meeting Area and Venue Selection',
    description = 'In the selection of the meeting area, the companys request and the purpose of the meeting are acted upon. In selecting the meeting place, the ease of transportation of the participants is considered. Having accommodation and meeting room in the same hotel is advantageous regarding operation and cost.  Determination of the most suitable region for the meeting. We are organizing a meeting room suitable for the number of participants and qualified for the meeting session layout. Supply meeting room equipment, and multimedia tools such as podium, microphone, light, stage, and projector. Collaboration with internationally known meeting areas in the country and abroad. Recruitment of adequate staff to guide and assist participants in their registration.'
}: RightTwoImageBottomTextCtaProps) {
    // URL'leri transform et (medya proxy kullanımı için)
    const leftImageUrl = leftImage?.url ? transformMediaUrl(leftImage.url, null) : null;
    const rightImageUrl = rightImage?.url ? transformMediaUrl(rightImage.url, null) : null;
    
    return (
        <motion.section
            className="flex flex-col"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
        >
            <div className="flex flex-row">
                <motion.div 
                    className="flex-1 flex flex-col justify-between"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ 
                        type: "spring" as const, 
                        stiffness: 80, 
                        damping: 20,
                        delay: 0.2
                    }}
                >
                    <motion.div 
                        className="pl-10 pt-[100px]"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="639.227" height="359.039" viewBox="0 0 639.227 359.039">
                            <path d="M639.554,76.527C540.328,4.106,408.505-20.33,283.475,22.509,132.378,74.278,33.7,209.736,21.469,359.362m0,0L.956,340.931m20.512,18.431,24.592-12.856" transform="translate(-0.622 -0.766)" fill="none" stroke="#022a3a" strokeWidth="1" strokeDasharray="10 10"/>
                        </svg>
                    </motion.div>
                    <motion.div 
                        className="w-9/12 mt-[140px]"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                    >
                        <div className="flex flex-col gap-10">
                            <motion.h2 
                                className="font-bold text-alsero-dark-gray text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5, duration: 0.6 }}
                                data-prop-path="title"
                            >
                                {title}
                            </motion.h2>
                            <motion.p 
                                className="text-alsero-light-gray text-base sm:text-lg md:text-xl leading-relaxed"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.6, duration: 0.6 }}
                                data-prop-path="description"
                            >
                                {description}
                            </motion.p>
                        </div>
                    </motion.div>
                </motion.div>
                <motion.div 
                    className="flex-1 flex flex-col items-start relative z-10"
                    initial={{ opacity: 0, x: 50, scale: 0.95 }}
                    whileInView={{ opacity: 1, x: 0, scale: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ 
                        type: "spring" as const, 
                        stiffness: 80, 
                        damping: 20,
                        delay: 0.3
                    }}
                >
                    {leftImageUrl && (
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            whileHover={{ rotate: -2 }}
                            className="relative z-10 -ml-[30%]"
                        >
                            <Image
                                src={leftImageUrl}
                                alt={leftImage?.alt || "left-img"}
                                width={445}
                                height={530}
                                className="h-[530px] object-cover -rotate-3"
                                unoptimized={leftImageUrl.startsWith('/api/media/proxy/')}
                            />
                        </motion.div>
                    )}
                    {rightImageUrl && (
                        <motion.div 
                            className="absolute top-1/2 -translate-y-1/2 rotate-3"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ 
                                type: "spring" as const,
                                stiffness: 100,
                                damping: 20,
                                delay: 0.5 
                            }}
                            whileHover={{ rotate: 2 }}
                        >
                            <Image
                                src={rightImageUrl}
                                alt={rightImage?.alt || "right-img"}
                                width={650}
                                height={820}
                                className="h-[820px] object-cover"
                                unoptimized={rightImageUrl.startsWith('/api/media/proxy/')}
                            />
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </motion.section>
    )
}